import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Waves, Wind, Smile, Sunset, Calendar, MapPin, Home, Coffee, CheckCircle2 } from 'lucide-react';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};
export function ResetRetreatPage() {
  return <div dir="rtl" className="font-['Heebo'] bg-slate-50 text-slate-800 min-h-screen overflow-x-hidden" data-id="element-1039" data-mp="jdolvltkadzk">
      {/* Hero Section */}
      <section className="relative h-[90vh] flex items-center justify-center overflow-hidden" data-id="element-1040" data-mp="aea2f6btb3ok">
        <div className="absolute inset-0 z-0" data-id="element-1041" data-mp="2q5fqn5mz8pik">
          <video autoPlay loop muted playsInline className="w-full h-full object-cover" data-id="element-1042" data-mp="2q4msuu5zxack">
            <source src="https://cdn.magicpatterns.com/uploads/aAuWxGLVcEb5PCo3LzHw7T/VIDEO-2026-04-21-10-42-44.mp4" type="video/mp4" data-id="element-1043" data-mp="vco5nhmk7ic3" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" data-id="element-1044" data-mp="15au8q2k3quij"></div>
        </div>
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto mt-16" data-id="element-1045" data-mp="10brdtaxu64oa">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8
        }} data-id="element-1046" data-mp="1ebiop2t234y2">
            <span className="inline-block py-1 px-3 rounded-full bg-teal-500/20 text-teal-200 border border-teal-500/30 text-sm font-medium mb-6 backdrop-blur-sm" data-id="element-1047" data-mp="1o3g30ifd2d10">
              4-6 ביוני, 2026 | חוף מעגן, כנרת
            </span>
            <h1 className="text-5xl md:text-7xl font-extrabold text-white mb-6 leading-tight" data-id="element-1048" data-mp="1t180dryryrcl">
              פשוט להיות. <br data-id="element-1049" data-mp="204o43r01aqkb" />
              <span className="text-teal-400" data-id="element-1050" data-mp="2057mt9xidize">ריטריט RESET</span>
            </h1>
            <p className="text-xl md:text-2xl text-slate-200 mb-10 font-light max-w-2xl mx-auto leading-relaxed" data-id="element-1051" data-mp="2540yqxx5d1xg">
              שלושה ימים של ניתוק מהרעש וחיבור לשקט, לכנרת, לגוף ולקהילה.
            </p>
          </motion.div>
        </div>
        {/* Scroll indicator */}
        <motion.div animate={{
        y: [0, 10, 0]
      }} transition={{
        repeat: Infinity,
        duration: 2
      }} className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/70 z-10" data-id="element-1052" data-mp="2iumispyc7hwx">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center p-1" data-id="element-1053" data-mp="2sqh5px9lfz5i">
            <div className="w-1.5 h-1.5 bg-white rounded-full" data-id="element-1054" data-mp="2xpkbxl1b8bf2"></div>
          </div>
        </motion.div>
      </section>

      {/* What Awaits Us Section */}
      <section className="py-24 px-4 bg-white" data-id="element-1055" data-mp="13eng3u7hoe42">
        <div className="max-w-6xl mx-auto" data-id="element-1056" data-mp="13fgpz41qvjqf">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-1057" data-mp="3go5hgui6sxwt">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-1058" data-mp="32wh0act2zj8z">
              מה מחכה לנו במים (ובחוף)?
            </h2>
            <div className="w-24 h-1 bg-teal-500 mx-auto rounded-full" data-id="element-1059" data-mp="3gc3ctv9d5mc"></div>
          </motion.div>

          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-1060" data-mp="3vm7gcre9wv7q">
            {[{
            image: 'https://cdn.magicpatterns.com/uploads/sZvN5qMq5z1g4itY3TLS7p/450c666a-127e-4210-8e53-b50e5ae8ca31.jpg',
            title: 'סאפ וחתירה',
            desc: 'חתירות בוקר בראי של הכנרת, שיפור טכניקה ועבודה על יציבות.'
          }, {
            image: 'https://cdn.magicpatterns.com/uploads/iB2BDqYpRmo1UfxAEHupva/7a450878-29f4-4003-be04-e530e563c8cd.jpg',
            title: 'ווינג-פויל (Wing Foil)',
            desc: 'הדרכת היכרות עם הלהיט החדש של עולם המים – תחושת תעופה מעל המים.'
          }, {
            image: 'https://cdn.magicpatterns.com/uploads/bbWSuM5Yyx2KKeVhp6v4TC/986ca47d-8d42-4eda-a1fa-b9a784920765.jpg',
            title: 'יוגלאטיס ותנועה',
            desc: 'תרגול מול הנוף הפתוח לחיזוק הגוף והרגעת הנפש.'
          }, {
            image: 'https://cdn.magicpatterns.com/uploads/9RhaMzBe737i8zTZnvdDuM/IMG_8179.jpg',
            title: 'זמן חוף ושקט',
            desc: 'מעגלי שיח בערב מול הנוף וזמן חופשי לעצמך.'
          }].map((feature, idx) => <motion.div key={idx} variants={fadeInUp} className="bg-slate-50 p-8 rounded-3xl text-center hover:shadow-xl transition-shadow border border-slate-100 flex flex-col items-center" data-id="element-1061" data-mp="2twu2uu6rdhev" data-mpi={idx}>
                <div className="w-full h-48 rounded-2xl overflow-hidden mb-6 shadow-sm" data-id="element-1062" data-mp="2xw2sbmurmt4m">
                  <img src={feature.image} alt={feature.title} className="w-full h-full object-cover" data-id="element-1063" data-mp="2a5yslqkp7diw" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-slate-800" data-id="element-1064" data-mp="2jwte2sq4e93a">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-1065" data-mp="2f4a2u2ewnq1n">{feature.desc}</p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-12 bg-slate-900 overflow-hidden" data-id="element-1066" data-mp="1mgmlqxcufhjr">
        <div className="flex gap-4 px-4 overflow-x-auto pb-8 snap-x snap-mandatory scrollbar-hide" data-id="element-1067" data-mp="3mjx5mm5260iu">
          {['https://cdn.magicpatterns.com/uploads/euveAznXLyoRgqWzJJM5d2/c5ba1ee6-439d-47d1-9f37-d431e61bd061.jpg', 'https://cdn.magicpatterns.com/uploads/q9hdMEFoFD2zoRq7GwyTmt/4e20683c-8dc6-493a-a623-9274c19ff803.jpg', 'https://cdn.magicpatterns.com/uploads/5GQjcjiGrdz6DgD6rNz6Xx/e74f9f3c-a78a-45c4-8dea-b0c7c0f86bca.jpg'].map((img, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: idx * 0.2
        }} className="min-w-[80vw] md:min-w-[400px] h-[300px] md:h-[400px] snap-center rounded-2xl overflow-hidden relative group" data-id="element-1068" data-mp="38j4embdytnkh" data-mpi={idx}>
              <img src={img} alt={`Retreat moment ${idx + 1}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-1069" data-mp="2hbchm503qjiu" />
              <div className="absolute inset-0 bg-teal-900/20 group-hover:bg-transparent transition-colors duration-500" data-id="element-1070" data-mp="28hhe11emp15b"></div>
            </motion.div>)}
        </div>
      </section>

      {/* Schedule Section */}
      <section className="py-24 px-4 bg-slate-50 relative overflow-hidden" data-id="element-1071" data-mp="1tm1qzgcgu5if">
        <div className="absolute top-40 -left-40 w-96 h-96 bg-blue-100 rounded-full blur-3xl opacity-50" data-id="element-1072" data-mp="1tow7iwscz66k"></div>
        <div className="max-w-4xl mx-auto relative z-10" data-id="element-1073" data-mp="1km4vovvkm8ee">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-20" data-id="element-1074" data-mp="1knpqmmlsrtcs">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-1075" data-mp="2nuk3q3vskbil">
              לוח זמנים מקוצר
            </h2>
            <div className="w-24 h-1 bg-teal-500 mx-auto rounded-full mb-6" data-id="element-1076" data-mp="2stwn2engf0gf"></div>
            <p className="text-slate-600 text-lg" data-id="element-1077" data-mp="2e58u8g4zt4u6">
              שלושה ימים מלאים בחוויות, מתוכננים בקפידה.
            </p>
          </motion.div>

          <div className="relative" data-id="element-1078" data-mp="2j28xkjagjthl">
            <div className="absolute right-4 md:right-1/2 top-0 bottom-0 w-1 bg-teal-100 transform md:translate-x-0.5" data-id="element-1079" data-mp="2542be0evfhxr"></div>
            <div className="space-y-12" data-id="element-1080" data-mp="295w85xly41sn">
              {[{
              day: 'יום חמישי',
              date: '4.6',
              title: 'הגעה והתאקלמות',
              content: 'התכנסות בצהריים, חתירת היכרות בשקיעה וארוחת ערב משותפת.',
              icon: Sunset
            }, {
              day: 'יום שישי',
              date: '5.6',
              title: 'מעמיקים פנימה',
              content: 'חתירת זריחה, סדנת יוגלאטיס, סדנאות טכניקת חתירה, היכרות עם ווינג-פויל וקבלת שבת.',
              icon: Waves
            }, {
              day: 'יום שבת',
              date: '6.6',
              title: 'סיכום ופרידה',
              content: 'תרגול בוקר, חתירה חגיגית לסיכום ופרידה.',
              icon: Smile
            }].map((item, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-50px'
            }} transition={{
              delay: idx * 0.2
            }} className={`relative flex flex-col md:flex-row items-start md:items-center gap-8 ${idx % 2 === 0 ? 'md:flex-row-reverse' : ''}`} data-id="element-1081" data-mp="21kk1r0dvd9pd" data-mpi={idx}>
                  <div className="absolute right-0 md:right-1/2 transform translate-x-3.5 md:translate-x-6 w-10 h-10 bg-white border-4 border-teal-400 rounded-full flex items-center justify-center z-10 shadow-md" data-id="element-1082" data-mp="1ne30mkvc7wod">
                    <div className="w-2.5 h-2.5 bg-teal-500 rounded-full" data-id="element-1083" data-mp="1dh7ylrzuve5c"></div>
                  </div>

                  <div className={`w-full md:w-1/2 pl-12 pr-12 md:px-12 flex ${idx % 2 === 0 ? 'md:justify-start' : 'md:justify-end'}`} data-id="element-1084" data-mp="2b35fxm2i1dfp">
                    <div className="text-right md:text-center" data-id="element-1085" data-mp="21dvlhzk6niof">
                      <span className="inline-block px-4 py-1.5 bg-teal-50 text-teal-700 rounded-full font-bold text-sm mb-2 border border-teal-100" data-id="element-1086" data-mp="21caugjx2uhvb">
                        {item.date}
                      </span>
                      <h3 className="text-2xl font-extrabold text-slate-800" data-id="element-1087" data-mp="2p3yd6ek6qmmi">
                        {item.day}
                      </h3>
                    </div>
                  </div>

                  <div className="w-full md:w-1/2 pl-4 pr-12 md:px-12" data-id="element-1088" data-mp="2xeyq49aon7j1">
                    <div className="bg-white rounded-2xl p-6 shadow-lg shadow-slate-200/50 border border-slate-100 hover:-translate-y-1 transition-transform duration-300" data-id="element-1089" data-mp="31epjmkrlg1r9">
                      <div className="mb-3" data-id="element-1090" data-mp="3bdetbq9c6fwf">
                        <h4 className="text-xl font-bold text-slate-800" data-id="element-1091" data-mp="3baakugou8xzy">
                          {item.title}
                        </h4>
                      </div>
                      <p className="text-slate-600 leading-relaxed" data-id="element-1092" data-mp="3kbqqyc8medyd">
                        {item.content}
                      </p>
                    </div>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Details & Pricing */}
      <section id="register" className="py-24 px-4 bg-white relative overflow-hidden" data-id="element-1093" data-mp="2yekcelsfxyk">
        <div className="absolute top-0 right-0 w-96 h-96 bg-teal-50 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 opacity-50" data-id="element-1094" data-mp="7tagnqy257hh"></div>
        <div className="max-w-6xl mx-auto relative z-10" data-id="element-1095" data-mp="39ellfo6vmziu">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mb-20" data-id="element-1096" data-mp="39bzd3mc5wfzk">
            <div className="text-center mb-12" data-id="element-1097" data-mp="zttmgmhi91z">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-1098" data-mp="3s3a566tmiyt1">
                פרטים טכניים
              </h2>
              <div className="w-24 h-1 bg-teal-500 mx-auto rounded-full" data-id="element-1099" data-mp="avex6j77bhdu"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1100" data-mp="5xv8epcvnm7n">
              <div className="bg-slate-50 rounded-3xl border border-slate-100 text-center hover:shadow-lg transition-shadow overflow-hidden flex flex-col" data-id="element-1101" data-mp="2cokyzvtzcv4p">
                <div className="h-48 w-full relative" data-id="element-1102" data-mp="n6foxzopmfhy">
                  <img src="https://cdn.magicpatterns.com/uploads/gaKJFduspmT4WMqMsV6Gs9/ריזורט-אפשר-גם-לגלריה-אם-חסר.jpg" alt="Sea of Galilee" className="w-full h-full object-cover" data-id="element-1103" data-mp="n6xw3rcwue4n" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent" data-id="element-1104" data-mp="if60v3it7k86"></div>
                </div>
                <div className="p-8 pt-6 flex-1" data-id="element-1105" data-mp="4f53hy1r6uxg">
                  <h3 className="text-xl font-bold text-slate-800 mb-3" data-id="element-1106" data-mp="8hjlkfxllqno">
                    מיקום
                  </h3>
                  <p className="text-slate-600" data-id="element-1107" data-mp="3qnxxk9cb3qjf">
                    חוף מעגן, כנרת. חוף פרטי ושקט.
                  </p>
                </div>
              </div>

              <div className="bg-slate-50 rounded-3xl border border-slate-100 text-center hover:shadow-lg transition-shadow overflow-hidden flex flex-col" data-id="element-1108" data-mp="3vo2eg0h4mna6">
                <div className="h-48 w-full relative" data-id="element-1109" data-mp="1ttjnk51tttmm">
                  <img src="https://cdn.magicpatterns.com/uploads/awHjGHHpAWW4fTgQ7Pwa9Y/חדר_משפחתי.jpg" alt="Family Room" className="w-full h-full object-cover" data-id="element-1110" data-mp="3py0x1jj1ipju" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent" data-id="element-1111" data-mp="3gvzxgg0c3rf0"></div>
                </div>
                <div className="p-8 pt-6 flex-1" data-id="element-1112" data-mp="3c0c9u51i36bm">
                  <h3 className="text-xl font-bold text-slate-800 mb-3" data-id="element-1113" data-mp="36zeqvq8o2892">
                    לינה
                  </h3>
                  <p className="text-slate-600" data-id="element-1114" data-mp="327n1vk55sal1">
                    <a href="https://www.maagan.co.il/" target="_blank" rel="noopener noreferrer" className="text-teal-600 font-medium hover:text-teal-700 underline underline-offset-4 decoration-teal-600/30 hover:decoration-teal-600 transition-all" data-id="element-1115" data-mp="2x9brn8ayby2a">
                      כפר הנופש, מעגן עדן
                    </a>{' '}
                    בקיבוץ מעגן. חדרים משפחתיים, מרווחים ומפנקים שמתאימים לשלושה
                    מבוגרים.
                  </p>
                </div>
              </div>

              <div className="bg-slate-50 rounded-3xl border border-slate-100 text-center hover:shadow-lg transition-shadow overflow-hidden flex flex-col" data-id="element-1116" data-mp="18pz5qowbzdtv">
                <div className="h-48 w-full relative" data-id="element-1117" data-mp="1cqrivt8bq2vf">
                  <img src="https://images.unsplash.com/photo-1533089860892-a7c6f0a88666?auto=format&fit=crop&q=80&w=800" alt="Rich Breakfast" className="w-full h-full object-cover" data-id="element-1118" data-mp="g1k3z427ms0h" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent" data-id="element-1119" data-mp="ozm3tga7jem6"></div>
                </div>
                <div className="p-8 pt-6 flex-1" data-id="element-1120" data-mp="3tg6rqtl8iy99">
                  <h3 className="text-xl font-bold text-slate-800 mb-3" data-id="element-1121" data-mp="3tfojeylastkz">
                    תזונה
                  </h3>
                  <p className="text-slate-600" data-id="element-1122" data-mp="19roms2f1dyr">
                    ארוחות בריאות ומזינות לאורך כל הריטריט: בוקר וערב.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Pricing Section */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="mt-24" data-id="element-1123" data-mp="1ebjoi28y6rj4">
            <div className="text-center mb-12" data-id="element-1124" data-mp="updks7sws6m8">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-1125" data-mp="uq6w7m2tbkap">
                עלויות והרשמה
              </h2>
              <p className="text-slate-600" data-id="element-1126" data-mp="2640x2vt26l5u">
                מספר המקומות מוגבל, הבטיחו את מקומכם מראש.
              </p>
            </div>

            <div className="max-w-3xl mx-auto" data-id="element-1127" data-mp="221e1qkx6ii3w">
              <div className="relative rounded-3xl border border-teal-500/30 shadow-2xl shadow-teal-900/20 flex flex-col overflow-hidden text-center" data-id="element-1128" data-mp="224s25nma8h8p">
                <div className="absolute inset-0 z-0" data-id="element-1129" data-mp="2em2t1xckg774">
                  <img src="https://cdn.magicpatterns.com/uploads/idDXetNMa2tFPXNmSZmXFC/pool-2.jpg" alt="Resort Pool View" className="w-full h-full object-cover" data-id="element-1130" data-mp="1znse5zp28ogs" />
                  <div className="absolute inset-0 bg-slate-900/85 backdrop-blur-[2px]" data-id="element-1131" data-mp="2sdhialx2h4mm"></div>
                </div>
                <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-teal-400 to-blue-500 z-20" data-id="element-1132" data-mp="2xazsefmtlpw7"></div>

                <div className="relative z-10 p-8 md:p-12 flex flex-col h-full" data-id="element-1133" data-mp="2ikk3oqv73zta">
                  <div className="mb-8" data-id="element-1134" data-mp="2nk50aumw7w2u">
                    <h3 className="text-4xl md:text-5xl font-extrabold text-white mb-3 drop-shadow-md" data-id="element-1135" data-mp="3bbkb7bsqjlpp">
                      הרשמה מוקדמת
                    </h3>
                    <div className="inline-block bg-teal-500/20 border border-teal-400/30 text-teal-300 text-sm font-bold px-4 py-1.5 rounded-full backdrop-blur-sm" data-id="element-1136" data-mp="3g3bs0um3ad2n">
                      בתוקף עד ה-10.5.2026
                    </div>
                  </div>

                  <p className="text-sm text-teal-200/70 mb-10 px-4 max-w-lg mx-auto" data-id="element-1137" data-mp="176o5vd9x7g4t">
                    הלינה בחדרי פרמיום גדולים ומרווחים. המחיר משתנה לפי מספר
                    המשתתפים בחדר.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-1138" data-mp="1yy3n0rspkann">
                    <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50 backdrop-blur-sm" data-id="element-1139" data-mp="1uy38z89iu1h4">
                      <div className="text-teal-200/80 text-lg font-medium mb-3" data-id="element-1140" data-mp="1pwnr1p2n4b2y">
                        מחיר לאדם (זוג בחדר)
                      </div>
                      <div className="flex items-baseline justify-center gap-3" data-id="element-1141" data-mp="eiij6kzb3h2c">
                        <span className="text-5xl font-extrabold text-teal-400" data-id="element-1142" data-mp="egfk7bg6edlh">
                          ₪2,950
                        </span>
                        <span className="text-2xl text-slate-500 line-through decoration-slate-500/50" data-id="element-1143" data-mp="3vm7l0pv2s6o5">
                          ₪3,200
                        </span>
                      </div>
                    </div>
                    <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50 backdrop-blur-sm" data-id="element-1144" data-mp="9cdec5nuygic">
                      <div className="text-teal-200/80 text-lg font-medium mb-3" data-id="element-1145" data-mp="e6rcgftwn1qp">
                        מחיר לאדם (שלישייה בחדר)
                      </div>
                      <div className="flex items-baseline justify-center gap-3" data-id="element-1146" data-mp="n4bf9t7f163f">
                        <span className="text-5xl font-extrabold text-teal-400" data-id="element-1147" data-mp="s3wfs828hmi9">
                          ₪2,450
                        </span>
                        <span className="text-2xl text-slate-500 line-through decoration-slate-500/50" data-id="element-1148" data-mp="x30pczt2lm94">
                          ₪2,700
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-slate-900/40 rounded-2xl p-6 md:p-8 mb-10 text-right backdrop-blur-sm border border-slate-800/50" data-id="element-1149" data-mp="16ttctgm9tfa5">
                    <h4 className="text-white font-bold mb-4 text-lg border-b border-slate-700 pb-2" data-id="element-1150" data-mp="1avy1s21rxnqg">
                      המחיר כולל:
                    </h4>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1151" data-mp="1kkod44ipeo24">
                      {['כל הסדנאות והפעילויות', 'השכרת סאפ, משוט וחגורת הצלה לכל האירוע', 'ארוחות בוקר וערב', 'בריכת שחייה וחוף רחצה', 'פינת קפה מפנקת לאורך כל האירוע'].map((item, i) => <li key={i} className="flex items-start gap-3 text-white" data-id="element-1152" data-mp="23kc0kk0oso32" data-mpi={i}>
                          <CheckCircle2 size={20} className="text-teal-400 shrink-0 mt-0.5" data-id="element-1153" data-mp="48spnzvtp5qr" />
                          <span className="leading-tight font-medium" data-id="element-1154" data-mp="1nurolt9fpm1">
                            {item}
                          </span>
                        </li>)}
                    </ul>
                    <a href="https://wa.me/972548800260" target="_blank" rel="noopener noreferrer" className="w-full bg-slate-800 hover:bg-slate-900 text-white text-lg font-bold py-4 rounded-xl transition-colors shadow-md text-center block mt-6" data-id="element-1155" data-mp="3nzs0goewmzcx">
                      להרשמה ותשלום כתבו לנו בווטסאפ
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 py-12 text-center border-t border-slate-800" data-id="element-1156" data-mp="2veqn17wm4m7z">
        <div className="max-w-4xl mx-auto px-4" data-id="element-1157" data-mp="2ra0wzzomy1uz">
          <h2 className="text-2xl font-bold text-white mb-4" data-id="element-1158" data-mp="2ss7va6fk7r6g">Retreat RESET</h2>
          <p className="mb-6" data-id="element-1159" data-mp="2xru8ifve17f9">פשוט להיות.</p>
          <div className="flex justify-center gap-6 mb-8" data-id="element-1160" data-mp="2xqsxgbedgkxj">
            <span className="flex items-center gap-2" data-id="element-1161" data-mp="2iuld9b2yrji9">
              <Calendar size={18} data-id="element-1162" data-mp="2iu1sznpucj12" /> 4-6 ביוני, 2026
            </span>
            <span className="flex items-center gap-2" data-id="element-1163" data-mp="2nsv8floouk3v">
              <MapPin size={18} data-id="element-1164" data-mp="2nvhflk7o2xlk" /> חוף מעגן
            </span>
          </div>
          <p className="text-sm opacity-60" data-id="element-1165" data-mp="2a0oqpv5w052b">© 2026 כל הזכויות שמורות.</p>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <a href="https://wa.me/972548800260" target="_blank" rel="noopener noreferrer" className="fixed bottom-6 left-6 z-50 bg-[#25D366] text-white p-4 rounded-full shadow-lg hover:scale-110 hover:shadow-xl transition-all duration-300 flex items-center justify-center group" aria-label="Contact on WhatsApp" data-id="element-1166" data-mp="204c1b9hdkddd">
        <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor" xmlns="http://www.w3.org/2000/svg" data-id="element-1167" data-mp="1v4hcdbh5txuy">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" data-id="element-1168" data-mp="17inf45j67vdg" />
        </svg>
        <span className="absolute right-full mr-4 bg-white text-slate-800 px-3 py-1.5 rounded-lg text-sm font-bold opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap shadow-md pointer-events-none" data-id="element-1169" data-mp="323gsuc403vqq">
          יש לכם שאלות? דברו איתי
        </span>
      </a>
    </div>;
}